import { useState, useRef, useEffect } from "react";
import { useAvatar } from "../../context/AvatarContext";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import AvatarDisplay from "../avatar/AvatarDisplay";
import AdultosVinculadosModal from "../child/AdultosVinculadosModal";
import { getChildInfo } from "../../hooks/api";
import howemakerBadgeBoy from "../../assets/badges/howemaker-badge-boy.png";
import howemakerBadgeGirl from "../../assets/badges/howemaker-badge-girl.png";

const LogoutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: 18, height: 18 }}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M18 15l3-3m0 0l-3-3m3 3H9" />
  </svg>
);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

export default function ChildProfileIcon() {
  const { avatarData } = useAvatar();
  const { language, setLanguage, t } = useLanguage();
  const { logout, user } = useAuth();
  // user.es_howemaker viene del login, pero esa respuesta la sirve una Lambda vieja y separada
  // que todavía no tiene este campo — se confirma acá pegándole directo al backend actualizado.
  const [isHowemaker, setIsHowemaker] = useState(user?.es_howemaker === true);
  const howemakerBadge = avatarData?.gender === "girl" ? howemakerBadgeGirl : howemakerBadgeBoy;
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLanguagePanel, setShowLanguagePanel] = useState(false);
  const [showAdultosModal, setShowAdultosModal] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const childId = localStorage.getItem("childId");
    if (!childId) return;
    getChildInfo(childId).then(info => {
      if (info && typeof info.es_howemaker === "boolean") setIsHowemaker(info.es_howemaker);
    });
  }, []);

  // Cerrar al hacer click fuera
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setShowLanguagePanel(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageSelect = (lang) => {
    setLanguage(lang);
    setMenuOpen(false);
    setShowLanguagePanel(false);
    // Persistir en DB para recuperar en cualquier dispositivo
    const childId = localStorage.getItem("childId");
    if (childId) {
      fetch(`${API_BASE_URL}/child/language`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ child_id: childId, language: lang }),
      }).catch(() => {}); // Silent fail — ya se guardó en localStorage
    }
  };

  return (
    <div
      ref={menuRef}
      style={{ position: "fixed", top: 16, right: 16, zIndex: 9999 }}
    >
      {/* Ícono avatar */}
      <button
        onClick={() => {
          setMenuOpen((prev) => !prev);
          setShowLanguagePanel(false);
        }}
        className="w-14 h-14 rounded-full border-4 border-white/60 shadow-lg backdrop-blur-lg flex items-center justify-center bg-white/30 hover:bg-white/40 transition overflow-hidden cursor-pointer relative"
        title={t("language.label")}
      >
        {avatarData ? (
          <div className="w-full h-full relative">
            <AvatarDisplay avatarData={avatarData} />
          </div>
        ) : (
          <span className="text-2xl">👤</span>
        )}
      </button>

      {/* Insignia Howemaker */}
      {isHowemaker && (
        <img
          src={howemakerBadge}
          alt={t("admin.howemakers.title")}
          title={t("admin.howemakers.title")}
          style={{
            position: "absolute",
            bottom: -6,
            right: -6,
            width: 28,
            height: 28,
            objectFit: "contain",
            filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.35))",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Dropdown menú */}
      {menuOpen && !showLanguagePanel && (
        <div
          style={{
            position: "absolute",
            top: 64,
            right: 0,
            background: "white",
            borderRadius: 12,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
            minWidth: 140,
            overflow: "hidden",
            border: "1px solid #e9d5ff",
          }}
        >
          <button
            onClick={() => { setMenuOpen(false); setShowAdultosModal(true); }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "12px 16px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 15,
              fontWeight: 600,
              color: "#5b21b6",
              textAlign: "left",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f3ff")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <span style={{ fontSize: 18 }}>👥</span>
            {t("piggyBank.trustedAdult.menuItem")}
          </button>

          <button
            onClick={() => setShowLanguagePanel(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "12px 16px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 15,
              fontWeight: 600,
              color: "#5b21b6",
              textAlign: "left",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f3ff")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <span style={{ fontSize: 18 }}>🌐</span>
            {t("language.label")}
          </button>

          <div style={{ height: 1, background: "#e9d5ff", margin: "0 12px" }} />

          <button
            onClick={() => { setMenuOpen(false); logout(); navigate("/"); }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "12px 16px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 15,
              fontWeight: 600,
              color: "#dc2626",
              textAlign: "left",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#fff1f2")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <LogoutIcon />
            {t("language.logout")}
          </button>
        </div>
      )}

      {/* Modal adultos vinculados */}
      {showAdultosModal && (
        <AdultosVinculadosModal onClose={() => setShowAdultosModal(false)} />
      )}

      {/* Panel de idioma */}
      {menuOpen && showLanguagePanel && (
        <div
          style={{
            position: "absolute",
            top: 64,
            right: 0,
            background: "white",
            borderRadius: 12,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
            minWidth: 160,
            overflow: "hidden",
            border: "1px solid #e9d5ff",
          }}
        >
          <div
            style={{
              padding: "10px 16px 6px",
              fontSize: 12,
              fontWeight: 700,
              color: "#9ca3af",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            {t("language.select")}
          </div>

          <button
            onClick={() => handleLanguageSelect("es")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "10px 16px",
              background: language === "es" ? "#f5f3ff" : "none",
              border: "none",
              cursor: "pointer",
              fontSize: 15,
              fontWeight: language === "es" ? 700 : 400,
              color: language === "es" ? "#5b21b6" : "#374151",
              textAlign: "left",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f3ff")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background =
                language === "es" ? "#f5f3ff" : "none")
            }
          >
            <span style={{ fontSize: 12, fontWeight: 700, background: '#7c3aed', color: 'white', borderRadius: 4, padding: '2px 5px' }}>ES</span>
            {t("language.spanish")}
            {language === "es" && (
              <span style={{ marginLeft: "auto", color: "#7c3aed" }}>✓</span>
            )}
          </button>

          <button
            onClick={() => handleLanguageSelect("en")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "10px 16px",
              background: language === "en" ? "#f5f3ff" : "none",
              border: "none",
              cursor: "pointer",
              fontSize: 15,
              fontWeight: language === "en" ? 700 : 400,
              color: language === "en" ? "#5b21b6" : "#374151",
              textAlign: "left",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f3ff")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background =
                language === "en" ? "#f5f3ff" : "none")
            }
          >
            <span style={{ fontSize: 12, fontWeight: 700, background: '#2563eb', color: 'white', borderRadius: 4, padding: '2px 5px' }}>EN</span>
            {t("language.english")}
            {language === "en" && (
              <span style={{ marginLeft: "auto", color: "#7c3aed" }}>✓</span>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
